import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  timesheets, dailyTimesheets,
  type TimesheetListItem, type DailyTimesheetListItem,
} from '@/api/timesheets';
import { orgs } from '@/api/catalogs';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';

const MONTHS = ['', 'Січень', 'Лютий', 'Березень', 'Квітень', 'Травень', 'Червень',
  'Липень', 'Серпень', 'Вересень', 'Жовтень', 'Листопад', 'Грудень'];

type Kind = 'all' | 'monthly' | 'daily';

// Уніфікований рядок списку (місячний або денний табель).
interface Row {
  id: string;
  kind: 'monthly' | 'daily';
  organization_id: string;
  document_number: string;
  period_label: string;   // «Березень 2025» або дата «03.03.2025»
  sort_key: string;
  posted: boolean;
  deletion_mark: boolean;
}

export default function TimesheetsPage() {
  const navigate = useNavigate();
  const now = new Date();
  const [kind, setKind] = useState<Kind>('all');
  const [orgId, setOrgId] = useState('');
  const [periodYear, setPeriodYear] = useState<number | ''>(now.getFullYear());
  const [periodMonth, setPeriodMonth] = useState<number | ''>(now.getMonth() + 1);

  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const monthly = useQuery({
    queryKey: ['timesheets', orgId, periodYear, periodMonth],
    queryFn: () => timesheets.list({
      limit: 500, organization_id: orgId || undefined,
      period_year: periodYear || undefined,
      period_month: periodMonth || undefined,
    }),
  });
  const daily = useQuery({
    queryKey: ['daily-timesheets', orgId],
    queryFn: () => dailyTimesheets.list({ limit: 500, organization_id: orgId || undefined }),
  });

  const rows = useMemo<Row[]>(() => {
    const out: Row[] = [];
    if (kind !== 'daily') {
      for (const m of (monthly.data?.items ?? []) as TimesheetListItem[]) {
        out.push({
          id: m.id, kind: 'monthly', organization_id: m.organization_id,
          document_number: m.document_number,
          period_label: `${MONTHS[m.period_month]} ${m.period_year}`,
          sort_key: `${m.period_year}-${String(m.period_month).padStart(2, '0')}`,
          posted: m.posted, deletion_mark: m.deletion_mark,
        });
      }
    }
    if (kind !== 'monthly') {
      for (const d of (daily.data?.items ?? []) as DailyTimesheetListItem[]) {
        // Денний табель фільтруємо по місяцю/року дати (backend вміє лише точну дату).
        const dd = new Date(d.day_date);
        if (periodYear !== '' && dd.getFullYear() !== periodYear) continue;
        if (periodMonth !== '' && dd.getMonth() + 1 !== periodMonth) continue;
        out.push({
          id: d.id, kind: 'daily', organization_id: d.organization_id,
          document_number: d.document_number,
          period_label: dd.toLocaleDateString('uk-UA'),
          sort_key: d.day_date,
          posted: d.posted, deletion_mark: d.deletion_mark,
        });
      }
    }
    return out;
  }, [kind, monthly.data, daily.data, periodYear, periodMonth]);

  const orgName = (id: string) => {
    const o = orgList.data?.items.find((x) => x.id === id);
    return o ? o.name : '';
  };

  const cols = useMemo<ColumnDef<Row>[]>(() => [
    {
      key: 'kind', header: 'Тип', width: 90, align: 'center',
      sortValue: (r) => r.kind,
      cell: (r) => r.kind === 'daily'
        ? <span style={{ color: '#2563eb' }}>Денний</span>
        : <span style={{ color: '#15803d' }}>Місячний</span>,
    },
    { key: 'document_number', header: '№', width: 140, importBadge: true,
      sortValue: (r) => r.document_number ?? '', cell: (r) => r.document_number },
    {
      key: 'period', header: 'Період / Дата', width: 170,
      sortValue: (r) => r.sort_key, cell: (r) => r.period_label,
    },
    {
      key: 'org', header: 'Юрособа',
      sortValue: (r) => orgName(r.organization_id), cell: (r) => orgName(r.organization_id),
    },
    {
      key: 'posted', header: 'Проведено', width: 120, align: 'center',
      sortValue: (r) => r.posted ? 1 : 0, cell: (r) => r.posted ? '✓ так' : 'ні',
    },
  ], [orgList.data]);

  function openRow(r: Row) {
    navigate(r.kind === 'daily' ? `/daily-timesheets/${r.id}` : `/timesheets/${r.id}`);
  }

  return (
    <div className="card">
      {/* Верхній рядок: заголовок + кнопки створення (праворуч). */}
      <div className="toolbar" style={{ alignItems: 'center' }}>
        <h2 style={{ margin: 0 }}>Табелі обліку робочого часу</h2>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
          <button className="primary" onClick={() => navigate('/timesheets/new')}>
            + Місячний
          </button>
          <button className="primary" onClick={() => navigate('/daily-timesheets/new')}>
            + Денний
          </button>
        </div>
      </div>

      {/* Фільтри — окремим рядком під заголовком (як на сторінці «Виплата
          нарахувань»): юрособа + період (місяць+рік) у полях з підписами. */}
      <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end',
        flexWrap: 'wrap', marginBottom: '0.75rem' }}>
        <div className="field" style={{ minWidth: 200, margin: 0 }}>
          <label>Організація</label>
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            <option value="">— усі —</option>
            {orgList.data?.items.map((o) => (
              <option key={o.id} value={o.id}>{o.code} — {o.name}</option>
            ))}
          </select>
        </div>
        <div className="field" style={{ margin: 0, minWidth: 200 }}>
          <label>Період</label>
          <div style={{ display: 'flex', gap: 4 }}>
            <select value={periodMonth} style={{ flex: 2 }}
              onChange={(e) => setPeriodMonth(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— усі —</option>
              {MONTHS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
            <select value={periodYear} style={{ flex: 1 }}
              onChange={(e) => setPeriodYear(e.target.value ? Number(e.target.value) : '')}>
              <option value="">— усі —</option>
              {Array.from({ length: 7 }, (_, i) => now.getFullYear() - 5 + i).map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Сегмент типу табеля (Усі/Місячні/Денні) — над таблицею ліворуч. */}
      <div style={{ display: 'flex', border: '1px solid var(--border)',
        borderRadius: 6, overflow: 'hidden', width: 'fit-content',
        marginBottom: '0.6rem' }}>
        {([['all', 'Усі'], ['monthly', 'Місячні'], ['daily', 'Денні']] as const).map(([k, l]) => (
          <button key={k} onClick={() => setKind(k)}
            style={{
              padding: '0.3rem 0.7rem', border: 'none', cursor: 'pointer', fontSize: '0.8rem',
              background: kind === k ? 'var(--primary)' : 'transparent',
              color: kind === k ? '#fff' : 'var(--text-secondary)',
            }}>
            {l}
          </button>
        ))}
      </div>

      <DataTable<Row>
        rows={rows}
        columns={cols}
        rowKey={(r) => `${r.kind}-${r.id}`}
        loading={monthly.isLoading || daily.isLoading}
        emptyText="Немає табелів."
        onRowDoubleClick={openRow}
        rowMuted={(r) => r.deletion_mark ? 0.4 : undefined}
        initialSortKey="period"
        initialSortDir="desc"
      />
    </div>
  );
}
